// ════════════════════════════════════════════════════════════════════════════
// 🔄 ReturnModal — Item-level return UI for past transactions
// ════════════════════════════════════════════════════════════════════════════
// Calls POST /api/transactions/:txRef/return
// Allows cashier to select which items are being returned with quantities,
// capture reason, refund amount, and customer communication notes.
// ════════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'motion/react';
import {
  RotateCcw, X, AlertTriangle, DollarSign, MessageSquare,
  ShoppingBag, Minus, Plus, CheckCircle2, FileText, Search,
  Package, PlusCircle, Trash2
} from 'lucide-react';

interface ReturnItem {
  product: { id: number; name: string; price: number; emoji?: string };
  qty: number;
}

interface InventoryProduct {
  id: number;
  name: string;
  price: number;
  emoji?: string;
  stockCount: number;
  unitType?: string;
}

interface ReturnModalProps {
  open: boolean;
  txRef: string | null;
  txItems: ReturnItem[];
  txCustomerId?: number | null;
  storeApiKey: string;
  onClose: () => void;
  onReturned: (txRef: string) => void;
}

const formatRs = (n: number) =>
  'Rs. ' + n.toLocaleString('en-LK', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Product search sub-component — searches inventory and lets user pick */
const ProductSearch: React.FC<{
  products: InventoryProduct[];
  onSelect: (p: InventoryProduct) => void;
  selectedNames: string[];
  placeholder: string;
}> = ({ products, onSelect, selectedNames, placeholder }) => {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const filtered = useMemo(() => {
    if (!q.trim()) return [];
    const lower = q.toLowerCase();
    return products.filter(
      p => p.name.toLowerCase().includes(lower) && !selectedNames.includes(p.name)
    ).slice(0, 10);
  }, [q, products, selectedNames]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} className="relative">
      <div className="relative">
        <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          type="text"
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          className="w-full pl-7 pr-3 py-2 rounded-lg border-2 border-slate-500 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20 outline-none text-[11px] placeholder:text-slate-500"
        />
      </div>
      {open && filtered.length > 0 && (
        <div className="absolute z-10 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-lg max-h-40 overflow-y-auto">
          {filtered.map(p => (
            <button
              key={p.id}
              type="button"
              onClick={() => { onSelect(p); setQ(''); setOpen(false); }}
              className="w-full flex items-center gap-2 px-3 py-2 hover:bg-sky-50 text-left transition-colors border-b border-slate-50 last:border-0"
            >
              <Package size={12} className="text-slate-400 shrink-0" />
              <span className="text-[11px] font-semibold text-slate-800 flex-1 truncate">{p.name}</span>
              <span className="text-[9px] font-mono text-slate-500 shrink-0">{formatRs(p.price)}</span>
              <span className="text-[8px] font-mono text-slate-400 shrink-0">({p.stockCount})</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const ReturnModal: React.FC<ReturnModalProps> = ({ open, txRef, txItems, txCustomerId, storeApiKey, onClose, onReturned }) => {
  // Per-item return quantities (keyed by product id)
  const [returnQtys, setReturnQtys] = useState<Record<number, number>>({});
  // Per-item restock preferences (keyed by product id)
  const [returnRestocks, setReturnRestocks] = useState<Record<number, boolean>>({});
  const [reason, setReason] = useState('');
  const [refundAmount, setRefundAmount] = useState('');
  const [customerTold, setCustomerTold] = useState('');
  const [customerBoughtMore, setCustomerBoughtMore] = useState(false);
  // 💳 Refund payment method
  const [refundMethod, setRefundMethod] = useState<'cash' | 'card' | 'mobile' | 'bank_transfer' | 'other'>('cash');
  // 💰 Refund to Store Credit
  const [refundToStoreCredit, setRefundToStoreCredit] = useState(false);
  // 🔄 Item exchange tracking
  const [exchangedItem, setExchangedItem] = useState(false);
  const [exchangeItemName, setExchangeItemName] = useState('');
  const [exchangeItemPrice, setExchangeItemPrice] = useState('');
  const [exchangeItemQty, setExchangeItemQty] = useState('1');
  const [exchangeItemUnitType, setExchangeItemUnitType] = useState('Pieces');
  const [exchangeProductId, setExchangeProductId] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // ⏱ Refund timing
  const [refundTiming, setRefundTiming] = useState<'immediate' | 'later'>('immediate');
  // 📅 Refund due date (for later refunds)
  const [refundDueDate, setRefundDueDate] = useState('');
  // 🧑 Refund given by
  const [refundedBy, setRefundedBy] = useState('');
  // 🧑 Refund handled by (different person - for later refunds)
  const [refundLaterBy, setRefundLaterBy] = useState('');

  // 🏪 Inventory for product search
  const [inventory, setInventory] = useState<InventoryProduct[]>([]);
  const [invLoading, setInvLoading] = useState(false);

  // 🛒 Additional items customer bought
  const [boughtMoreItems, setBoughtMoreItems] = useState<{ name: string; price: number; qty: number; unitType?: string }[]>([]);

  useEffect(() => {
    if (open && storeApiKey) {
      setInvLoading(true);
      fetch(`/api/inventory?apiKey=${encodeURIComponent(storeApiKey)}`)
        .then(r => r.json())
        .then(data => {
          const prods: any[] = data.inventory || data.products || data || [];
          setInventory(prods.map((p: any) => ({
            id: p.id,
            name: p.name || p.productName,
            price: parseFloat(p.price || p.sellingPrice || 0),
            unitType: p.unitType || 'Pieces',
            emoji: p.emoji,
            stockCount: parseInt(p.stockCount || p.stock || 0),
          })));
        })
        .catch(() => { /* silent */ })
        .finally(() => setInvLoading(false));
    }
  }, [open, storeApiKey]);

  useEffect(() => {
    if (open) {
      // Reset state when modal opens
      const initial: Record<number, number> = {};
      const initialRestocks: Record<number, boolean> = {};
      txItems.forEach(item => {
        initial[item.product.id] = 0;
        initialRestocks[item.product.id] = true; // default to restock = true
      });
      setReturnQtys(initial);
      setReturnRestocks(initialRestocks);
      setReason('');
      setRefundAmount('');
      setCustomerTold('');
      setCustomerBoughtMore(false);
      setRefundMethod('cash');
      setRefundToStoreCredit(false);
      setExchangedItem(false);
      setExchangeItemName('');
      setExchangeItemPrice('');
      setExchangeItemQty('1');
      setExchangeItemUnitType('Pieces');
      setExchangeProductId(null);
      setBoughtMoreItems([]);
      setError(null);
      setSubmitting(false);
      setRefundTiming('immediate');
      setRefundDueDate('');
      setRefundedBy(localStorage.getItem('kangavena:cashierName') || '');
      setRefundLaterBy('');
    }
  }, [open, txItems]);

  const totalRefund = useMemo(() => {
    let total = 0;
    txItems.forEach(item => {
      const qty = returnQtys[item.product.id] || 0;
      total += qty * item.product.price;
    });
    return total;
  }, [returnQtys, txItems]);

  const hasReturns = useMemo(() => {
    return Object.values(returnQtys).some((q: any) => Number(q) > 0);
  }, [returnQtys]);

  const handleQtyChange = (productId: number, delta: number) => {
    setReturnQtys(prev => {
      const item = txItems.find(i => i.product.id === productId);
      if (!item) return prev;
      const current = prev[productId] || 0;
      const next = Math.max(0, Math.min(item.qty, current + delta));
      return { ...prev, [productId]: next };
    });
  };

  const submit = async () => {
    if (!reason.trim()) {
      setError('Reason for return is required');
      return;
    }

    const returning = txItems
      .filter(item => (returnQtys[item.product.id] || 0) > 0)
      .map(item => ({
        productId: item.product.id,
        productName: item.product.name,
        qty: returnQtys[item.product.id] || 0,
        unitPrice: item.product.price,
        restock: !!returnRestocks[item.product.id]
      }));

    if (returning.length === 0) {
      setError('Select at least one item to return');
      return;
    }

    const token = localStorage.getItem('kangavena:cashierToken');
    if (!token) {
      setError('Manager sign-in required');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const res = await fetch(`/api/transactions/${encodeURIComponent(txRef || '')}/return`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-store-api-key': storeApiKey,
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          items: returning,
          reason: reason.trim(),
          refundAmount: refundAmount ? parseFloat(refundAmount) : null,
          refundMethod,
          refundToStoreCredit: refundToStoreCredit && !!txCustomerId ? true : undefined,
          exchangeItem: exchangedItem && exchangeItemName.trim()
            ? { productId: exchangeProductId, name: exchangeItemName.trim(), price: parseFloat(exchangeItemPrice) || 0, qty: parseFloat(exchangeItemQty) || 1, unitType: exchangeItemUnitType }
            : null,
          customerTold: customerTold.trim() || undefined,
          customerBoughtMore: customerBoughtMore ? boughtMoreItems : false,
          refundTiming: refundTiming,
          refundDueDate: refundTiming === 'later' ? refundDueDate || null : null,
          refundLaterBy: refundTiming === 'later' ? refundLaterBy.trim() || null : null,
          refundedBy: refundedBy.trim() || undefined,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data?.error || `Return failed (HTTP ${res.status})`);
        return;
      }
      onReturned(txRef || '');
    } catch {
      setError('Cannot reach server');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open || !txRef) return null;

  const addBoughtItem = (p: InventoryProduct) => {
    setBoughtMoreItems(prev => [...prev, { name: p.name, price: p.price, qty: 1, unitType: p.unitType || 'Pieces' }]);
  };
  const removeBoughtItem = (idx: number) => {
    setBoughtMoreItems(prev => prev.filter((_, i) => i !== idx));
  };
  const setBoughtQty = (idx: number, val: number) => {
    setBoughtMoreItems(prev => prev.map((item, i) =>
      i === idx ? { ...item, qty: Math.max(0.001, val || 0) } : item
    ));
  };
  const addExchangeProduct = (p: InventoryProduct) => {
    setExchangeItemName(p.name);
    setExchangeItemPrice(String(p.price));
    setExchangeItemUnitType(p.unitType || 'Pieces');
    setExchangeProductId(p.id);
  };

  const selectedExchangeNames = exchangeItemName.trim() ? [exchangeItemName] : [];
  const boughtNames = boughtMoreItems.map(i => i.name);

  return createPortal(
    <AnimatePresence>
      <motion.div
        key="return-bg"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[10001] bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-4"
        onClick={(e) => { if (e.target === e.currentTarget && !submitting) onClose(); }}
      >
        <motion.div
          key="return-card"
          initial={{ scale: 0.95, y: 12, opacity: 0 }}
          animate={{ scale: 1, y: 0, opacity: 1 }}
          exit={{ scale: 0.95, y: 12, opacity: 0 }}
          transition={{ duration: 0.18 }}
          className="w-full max-w-lg bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden flex flex-col max-h-[90vh]"
        >
          {/* Header */}
          <div className="flex items-start gap-3 px-5 pt-5 pb-3 border-b border-slate-100 shrink-0">
            <div className="w-10 h-10 rounded-xl bg-amber-50 border border-amber-200 flex items-center justify-center shrink-0">
              <RotateCcw size={18} className="text-amber-500" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-[14px] font-black text-slate-800">Process Return</h3>
              <p className="text-[11px] text-slate-500 mt-0.5 font-mono">{txRef}</p>
            </div>
            <button
              onClick={() => !submitting && onClose()}
              className="w-7 h-7 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-400 hover:text-slate-700 transition-colors shrink-0"
              aria-label="Close"
            >
              <X size={14} />
            </button>
          </div>

          {/* Body — scrollable */}
          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
            {/* Items Selection */}
            <div>
              <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-1">
                <ShoppingBag size={10} /> Select Items to Return
              </label>
              <div className="space-y-2">
                {txItems.map((item) => {
                  const returnQty = returnQtys[item.product.id] || 0;
                  return (
                    <div
                      key={item.product.id}
                      className={`flex flex-col gap-2 p-3 rounded-xl border transition-all ${
                        returnQty > 0
                          ? 'bg-amber-50/70 border-amber-300 shadow-sm'
                          : 'bg-slate-50 border-slate-200'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-base shrink-0">{item.product.emoji || '📦'}</span>
                        <div className="flex-1 min-w-0">
                          <p className="text-[11px] font-bold text-slate-800 truncate">{item.product.name}</p>
                          <p className="text-[9px] text-slate-500 font-mono mt-0.5">
                            {formatRs(item.product.price)} each · {item.qty} purchased
                          </p>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <button
                            type="button"
                            onClick={() => handleQtyChange(item.product.id, -1)}
                            disabled={returnQty <= 0}
                            className="w-7 h-7 rounded-lg bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-500 hover:text-slate-700 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                          >
                            <Minus size={11} />
                          </button>
                          <span className="w-7 text-center text-[11px] font-black text-slate-800 font-mono">
                            {returnQty}
                          </span>
                          <button
                            type="button"
                            onClick={() => handleQtyChange(item.product.id, 1)}
                            disabled={returnQty >= item.qty}
                            className="w-7 h-7 rounded-lg bg-amber-100 hover:bg-amber-200 border border-amber-200 flex items-center justify-center text-amber-600 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                          >
                            <Plus size={11} />
                          </button>
                        </div>
                        {returnQty > 0 && (
                          <div className="text-[11px] font-black font-mono text-amber-700 w-16 text-right shrink-0">
                            {formatRs(returnQty * item.product.price)}
                          </div>
                        )}
                      </div>

                      {returnQty > 0 && (
                        <div className="flex items-center justify-between border-t border-amber-200/50 pt-2 mt-1">
                          <span className="text-[9px] text-slate-500 font-semibold uppercase">Restock to Inventory?</span>
                          <div className="flex items-center gap-1">
                            <button
                              type="button"
                              onClick={() => setReturnRestocks(prev => ({ ...prev, [item.product.id]: true }))}
                              className={`px-2 py-0.5 rounded text-[8px] font-bold transition-all ${
                                returnRestocks[item.product.id]
                                  ? 'bg-emerald-500 text-white'
                                  : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                              }`}
                            >
                              Yes
                            </button>
                            <button
                              type="button"
                              onClick={() => setReturnRestocks(prev => ({ ...prev, [item.product.id]: false }))}
                              className={`px-2 py-0.5 rounded text-[8px] font-bold transition-all ${
                                !returnRestocks[item.product.id]
                                  ? 'bg-rose-500 text-white'
                                  : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                              }`}
                            >
                              No
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Refund Summary */}
            {hasReturns && (
              <div className="rounded-xl bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 p-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <DollarSign size={14} className="text-amber-600" />
                    <span className="text-[11px] font-bold text-amber-800">Total Refund Value</span>
                  </div>
                  <span className="text-[15px] font-black font-mono text-amber-700">{formatRs(totalRefund)}</span>
                </div>
              </div>
            )}

            {/* Reason */}
            <div>
              <label className="block text-[10px] font-bold text-slate-600 uppercase tracking-wider mb-1.5 flex items-center gap-1">
                <FileText size={10} /> Reason for Return *
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Damaged item / Wrong product / Customer changed mind / etc."
                rows={2}
                className="w-full px-3 py-2 rounded-xl border-2 border-slate-500 focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 outline-none text-[12px] text-slate-800 resize-none"
              />
            </div>

            {/* Refund Amount */}
            <div>
              <label className="block text-[10px] font-bold text-slate-600 uppercase tracking-wider mb-1.5 flex items-center gap-1">
                <DollarSign size={10} /> Refund Amount (Rs.)
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={refundAmount}
                onChange={(e) => setRefundAmount(e.target.value)}
                placeholder={`Auto-calculated: ${formatRs(totalRefund)} — override if partial refund`}
                className="w-full px-3 py-2.5 rounded-xl border-2 border-slate-500 focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 outline-none text-[12px] font-mono text-slate-900 disabled:text-slate-900 disabled:opacity-100 disabled:bg-slate-100 placeholder:text-slate-500"
              />
              <p className="text-[9px] text-slate-500 mt-1">
                Leave empty to refund the full value of selected items (Rs. {totalRefund.toLocaleString()})
              </p>
            </div>

            {/* 💳 Refund Payment Method */}
            <div>
              <label className="block text-[10px] font-bold text-slate-600 uppercase tracking-wider mb-1.5 flex items-center gap-1">
                <DollarSign size={10} /> Refund Payment Method
              </label>
              {!refundToStoreCredit && (
                <select
                  value={refundMethod}
                  onChange={(e) => setRefundMethod(e.target.value as any)}
                  className="w-full px-3 py-2.5 rounded-xl border-2 border-slate-500 focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 outline-none text-[12px] bg-white text-slate-900 placeholder:text-slate-500"
                >
                  <option value="cash">Cash</option>
                  <option value="card">Card</option>
                  <option value="mobile">Mobile Payment</option>
                  <option value="bank_transfer">Bank Transfer</option>
                  <option value="other">Other</option>
                </select>
              )}
            </div>

            {/* 💰 Refund to Store Credit Toggle */}
            <div
              className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${
                !txCustomerId
                  ? 'bg-slate-100 border-slate-200 opacity-60 cursor-not-allowed'
                  : refundToStoreCredit
                    ? 'bg-emerald-50 border-emerald-300'
                    : 'bg-slate-50 border-slate-200 hover:border-slate-300'
              }`}
              onClick={() => { if (txCustomerId) setRefundToStoreCredit(!refundToStoreCredit); }}
            >
              <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-colors ${
                refundToStoreCredit
                  ? 'bg-emerald-500 border-emerald-500 text-white'
                  : 'border-slate-300 bg-white'
              }`}>
                {refundToStoreCredit && <CheckCircle2 size={14} />}
              </div>
              <div className="flex-1">
                <p className="text-[11px] font-bold text-slate-800">Refund to Store Credit</p>
                {!txCustomerId ? (
                  <p className="text-[9px] text-rose-500 font-semibold">⚠ No customer attached to this receipt</p>
                ) : (
                  <p className="text-[9px] text-slate-500">Credit Rs. {totalRefund.toLocaleString()} to customer's balance</p>
                )}
              </div>
              <DollarSign size={16} className={`shrink-0 ${refundToStoreCredit ? 'text-emerald-500' : 'text-slate-300'}`} />
            </div>

            {/* ⏱ Refund Timing */}
            <div>
              <label className="block text-[10px] font-bold text-slate-600 uppercase tracking-wider mb-1.5 flex items-center gap-1">
                <DollarSign size={10} /> Refund Timing
              </label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setRefundTiming('immediate')}
                  className={`flex-1 py-2.5 rounded-xl border-2 text-[11px] font-bold uppercase tracking-wider transition-all ${
                    refundTiming === 'immediate'
                      ? 'bg-amber-50 border-amber-400 text-amber-700 shadow-sm'
                      : 'bg-white border-slate-300 text-slate-500 hover:border-slate-400'
                  }`}
                >
                  Immediate Refund
                </button>
                <button
                  type="button"
                  onClick={() => setRefundTiming('later')}
                  className={`flex-1 py-2.5 rounded-xl border-2 text-[11px] font-bold uppercase tracking-wider transition-all ${
                    refundTiming === 'later'
                      ? 'bg-sky-50 border-sky-400 text-sky-700 shadow-sm'
                      : 'bg-white border-slate-300 text-slate-500 hover:border-slate-400'
                  }`}
                >
                  Refund Later
                </button>
              </div>
              <p className="text-[9px] text-slate-500 mt-1">
                {refundTiming === 'immediate' ? 'Cash is given to customer now' : 'Customer will be paid later'}
              </p>
            </div>

            {refundTiming === 'later' && (
              <div className="pl-4 space-y-3 border-l-2 border-sky-300">
                <div>
                  <label className="block text-[10px] font-bold text-slate-600 uppercase tracking-wider mb-1.5 flex items-center gap-1">
                    <FileText size={10} /> Expected Refund Date
                  </label>
                  <input
                    type="date"
                    value={refundDueDate}
                    onChange={(e) => setRefundDueDate(e.target.value)}
                    className="w-full px-3 py-2.5 rounded-xl border-2 border-slate-500 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20 outline-none text-[12px] text-slate-800"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-slate-600 uppercase tracking-wider mb-1.5 flex items-center gap-1">
                    <FileText size={10} /> Refund Handled By
                  </label>
                  <input
                    type="text"
                    value={refundLaterBy}
                    onChange={(e) => setRefundLaterBy(e.target.value)}
                    placeholder="Who will give the refund to the customer"
                    className="w-full px-3 py-2.5 rounded-xl border-2 border-slate-500 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20 outline-none text-[12px] text-slate-800"
                  />
                  <p className="text-[9px] text-slate-500 mt-1">Different person handling the refund later</p>
                </div>
              </div>
            )}

            {/* 🧑 Refund Given By */}
            <div>
              <label className="block text-[10px] font-bold text-slate-600 uppercase tracking-wider mb-1.5 flex items-center gap-1">
                <FileText size={10} /> Refund Given By
              </label>
              <input
                type="text"
                value={refundedBy}
                onChange={(e) => setRefundedBy(e.target.value)}
                placeholder="Who gave the refund to the customer"
                className="w-full px-3 py-2.5 rounded-xl border-2 border-slate-500 focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 outline-none text-[12px] text-slate-800"
              />
            </div>

            {/* Customer Communication */}
            <div>
              <label className="block text-[10px] font-bold text-slate-600 uppercase tracking-wider mb-1.5 flex items-center gap-1">
                <MessageSquare size={10} /> What was told to customer
              </label>
              <textarea
                value={customerTold}
                onChange={(e) => setCustomerTold(e.target.value)}
                placeholder="e.g. 'We will process the refund within 3-5 business days'"
                rows={1}
                className="w-full px-3 py-2 rounded-xl border-2 border-slate-500 focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 outline-none text-[12px] text-slate-800 resize-none"
              />
            </div>

            {/* 🛒 Customer Bought More — with product search */}
            <div
              className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${
                customerBoughtMore
                  ? 'bg-emerald-50 border-emerald-300'
                  : 'bg-slate-50 border-slate-200 hover:border-slate-300'
              }`}
              onClick={() => setCustomerBoughtMore(!customerBoughtMore)}
            >
              <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-colors ${
                customerBoughtMore
                  ? 'bg-emerald-500 border-emerald-500 text-white'
                  : 'border-slate-300 bg-white'
              }`}>
                {customerBoughtMore && <CheckCircle2 size={14} />}
              </div>
              <div className="flex-1">
                <p className="text-[11px] font-bold text-slate-800">Customer bought additional items</p>
                <p className="text-[9px] text-slate-500">They exchanged or purchased something new</p>
              </div>
              <ShoppingBag size={16} className={`shrink-0 ${customerBoughtMore ? 'text-emerald-500' : 'text-slate-300'}`} />
            </div>

            {customerBoughtMore && (
              <div className="pl-4 space-y-2 border-l-2 border-emerald-200">
                <ProductSearch
                  products={inventory}
                  onSelect={addBoughtItem}
                  selectedNames={boughtNames}
                  placeholder="Search product customer bought…"
                />
                {boughtMoreItems.length > 0 && (
                  <div className="space-y-1.5">
                    {boughtMoreItems.map((item, idx) => (
                      <div key={idx} className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-emerald-50 border border-emerald-200">
                        <Package size={11} className="text-emerald-600 shrink-0" />
                        <span className="text-[11px] font-semibold text-slate-800 flex-1 truncate">{item.name}</span>
                        <div className="flex items-center gap-1 shrink-0">
                          <input
                            type="number"
                            min="0.001"
                            step="0.001"
                            value={item.qty}
                            onChange={(e) => setBoughtQty(idx, parseFloat(e.target.value) || 0)}
                            className="w-16 px-1.5 py-1 rounded border-2 border-emerald-400 text-[11px] font-black font-mono text-emerald-800 text-center outline-none focus:border-emerald-500 bg-white"
                          />
                          <span className="text-[8px] text-emerald-600 font-semibold uppercase">{item.unitType === 'Kg' ? 'Kg' : item.unitType === 'Liters' ? 'L' : 'pcs'}</span>
                        </div>
                        <span className="text-[10px] font-mono text-emerald-700 font-bold w-16 text-right">{formatRs(item.price * item.qty)}</span>
                        <button
                          onClick={() => removeBoughtItem(idx)}
                          className="w-5 h-5 rounded bg-emerald-200 hover:bg-emerald-300 flex items-center justify-center text-emerald-700 transition-colors shrink-0"
                        >
                          <X size={10} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* 🔄 Item Exchange — with inventory search */}
            <div
              className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${
                exchangedItem
                  ? 'bg-sky-50 border-sky-300'
                  : 'bg-slate-50 border-slate-200 hover:border-slate-300'
              }`}
              onClick={() => setExchangedItem(!exchangedItem)}
            >
              <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-colors ${
                exchangedItem
                  ? 'bg-sky-500 border-sky-500 text-white'
                  : 'border-slate-300 bg-white'
              }`}>
                {exchangedItem && <CheckCircle2 size={14} />}
              </div>
              <div className="flex-1">
                <p className="text-[11px] font-bold text-slate-800">Customer exchanged for another item</p>
                <p className="text-[9px] text-slate-500">Item was swapped for a different product</p>
              </div>
              <ShoppingBag size={16} className={`shrink-0 ${exchangedItem ? 'text-sky-500' : 'text-slate-300'}`} />
            </div>

            {exchangedItem && (
              <div className="pl-4 space-y-2 border-l-2 border-sky-200">
                <ProductSearch
                  products={inventory}
                  onSelect={addExchangeProduct}
                  selectedNames={selectedExchangeNames}
                  placeholder="Search for exchanged product…"
                />
                {exchangeItemName.trim() && (
                  <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-sky-50 border border-sky-200">
                    <Package size={11} className="text-sky-600 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <input
                        type="text"
                        value={exchangeItemName}
                        onChange={(e) => setExchangeItemName(e.target.value)}
                        className="w-full bg-transparent text-[11px] font-semibold text-slate-800 outline-none"
                      />
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <input
                        type="number"
                        min="0.001"
                        step="0.001"
                        value={exchangeItemQty}
                        onChange={(e) => setExchangeItemQty(e.target.value)}
                        className="w-16 px-1.5 py-1 rounded border-2 border-sky-400 text-[11px] font-black font-mono text-sky-800 text-center outline-none focus:border-sky-500 bg-white"
                      />
                      <span className="text-[8px] text-sky-600 font-semibold uppercase">{exchangeItemUnitType === 'Kg' ? 'Kg' : exchangeItemUnitType === 'Liters' ? 'L' : 'pcs'}</span>
                    </div>
                    <div className="w-24 shrink-0">
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={exchangeItemPrice}
                        onChange={(e) => setExchangeItemPrice(e.target.value)}
                        placeholder="Price"
                        className="w-full px-2 py-1 rounded border-2 border-sky-400 text-[10px] font-mono text-right outline-none focus:border-sky-500 text-slate-800"
                      />
                    </div>
                    <button
                      onClick={() => { setExchangeItemName(''); setExchangeItemPrice(''); setExchangeItemQty('1'); setExchangeItemUnitType('Pieces'); setExchangeProductId(null); }}
                      className="w-5 h-5 rounded bg-sky-200 hover:bg-sky-300 flex items-center justify-center text-sky-700 transition-colors shrink-0"
                    >
                      <X size={10} />
                    </button>
                  </div>
                )}
              </div>
            )}

            {error && (
              <div className="text-[11px] text-rose-600 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">
                {error}
              </div>
            )}
          </div>

          {/* Buttons */}
          <div className="flex gap-2 px-5 pb-5 shrink-0 border-t border-slate-100 pt-4">
            <button
              onClick={() => !submitting && onClose()}
              disabled={submitting}
              className="flex-1 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-[12px] font-bold uppercase tracking-wider transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={submit}
              disabled={submitting || !hasReturns || !reason.trim()}
              className="flex-1 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-600 text-white text-[12px] font-bold uppercase tracking-wider shadow-md shadow-amber-500/20 disabled:opacity-40 disabled:cursor-not-allowed active:scale-[0.97] transition-all flex items-center justify-center gap-1.5"
            >
              {submitting ? (
                <>Processing…</>
              ) : (
                <><RotateCcw size={12} /> Process Return</>
              )}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
};

export default ReturnModal;
